import { faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import "./CategorySelector.scss";
import DropdownAtom from "../../../../components/atoms/dropdown/dropdown";
import {
    selectIsRejectedInvoice,
    setFormField,
    setIsRejectedInvoice,
} from "../../../../store/slices/invoiceV2Slice";
import { selectProjectDetails } from "../../../../store/slices/projectDetailsSlice";
import { selectTicketCategoryFilter } from "../../../../store/slices/sharedFilterSlice";

const CategorySelector = memo(({ value, options, onChange, error, disabled }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Global state for rejected invoice toggle (connected to V2 slice)
    const isRejectedInvoice = useSelector(selectIsRejectedInvoice);

    // Get project details to access project ID - memoized
    const projectId = useSelector(selectProjectDetails)?.id;

    // Get the saved ticket category from shared filters - memoized
    const savedTicketCategoryId = useSelector((state) =>
        selectTicketCategoryFilter(state, projectId),
    );

    // Memoize disabled categories count calculation
    const disabledCategoriesCount = useMemo(() => {
        return options.filter((cat) => cat.isDisabled).length;
    }, [options]);

    // Memoized warning renderer
    const renderDisabledCategoriesWarning = useCallback(() => {
        if (disabledCategoriesCount > 0) {
            return (
                <div className="text-warning small mt-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    {t("invoice.ticket-categories-no-lv-warning", {
                        count: disabledCategoriesCount,
                    })}
                </div>
            );
        }
        return null;
    }, [disabledCategoriesCount, t]);

    // Memoized rejected invoice toggle handler
    const handleRejectedToggle = useCallback((e) => {
        dispatch(setIsRejectedInvoice(e.target.checked));
    }, [dispatch]);

    // Auto-select saved category effect with optimized dependencies
    useEffect(() => {
        if (!savedTicketCategoryId || value || !options.length || disabled) {
            return;
        }

        const savedOption = options.find(
            (option) => option.value === savedTicketCategoryId,
        );

        if (savedOption && !savedOption.isDisabled) {
            try {
                // Call onChange handler
                const event = { target: { value: savedTicketCategoryId } };
                onChange?.(event);

                // Update form field
                dispatch(
                    setFormField({
                        field: "projectTicketCategoryId",
                        value: savedTicketCategoryId,
                        skipDirtyFlag: true,
                    }),
                );
            } catch (error) {
                console.error("Error setting default ticket category:", error);
            }
        }
    }, [savedTicketCategoryId, value, options, disabled, onChange, dispatch]);

    return (
        <div className="mb-4 ml-2">
            <div className="d-flex align-items-center gap-3">
                <div className="cs-dropdown-container">
                    <DropdownAtom
                        label={t("invoice.ticket-category")}
                        placeholder={t("invoice.select-ticket-category")}
                        value={value}
                        options={options}
                        onChange={onChange}
                        error={error}
                        required={true}
                        disabled={disabled}
                    />
                    <div>{renderDisabledCategoriesWarning()}</div>
                </div>

                {/* Show rejected invoice toggle when a ticket category is selected */}
                {value && !disabled && (
                    <div className="cs-toggle-container">
                        <Form.Check
                            type="switch"
                            id="rejected-invoice-toggle-v2"
                            label={
                                <span className="fs-6 fw-medium text-danger">
                                    {t("invoice.is-rejected-invoice")}
                                </span>
                            }
                            checked={isRejectedInvoice}
                            onChange={handleRejectedToggle}
                            disabled={disabled}
                            className="custom-switch form-switch-lg"
                        />
                    </div>
                )}
            </div>

            {/* Show rejected invoice info when toggle is enabled */}
            {value && !disabled && isRejectedInvoice && (
                <div className="mt-3 p-3 border border-danger rounded bg-danger bg-opacity-10">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                    {t("invoice.rejected-invoice-info")}
                </div>
            )}
        </div>
    );
});

CategorySelector.displayName = "CategorySelector";

export default CategorySelector;
